import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { classificationToken, type Classification, type NodeKind, nodeKindToken } from '../tokens';
import { CloseIcon } from '../internal/icons';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Tag.module.css';

export type TagVariant = 'subtle' | 'solid' | 'outline';

export interface TagProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The meaning the colour encodes. Defaults to grey (`unclassified`). */
  classification?: Classification;
  /**
   * Convenience for catalogue items: resolves the classification rail of an
   * ontology node kind (`tool` → artifact, `agentSkill` → skill, …).
   */
  kind?: NodeKind;
  variant?: TagVariant;
  size?: 'sm' | 'md';
  /**
   * The non-colour channel. A canonical id or letter code (`S2`, `APP`, `G5`)
   * printed in mono beside the label, so the tag still classifies under forced
   * colours and for a colour-blind reader.
   */
  code?: string;
  onDismiss?: () => void;
  dismissLabel?: string;
  children: ReactNode;
}

/**
 * A classification chip. Colour here is never decorative — §6.2: colour
 * classifies or it is grey.
 */
export const Tag = forwardRef<HTMLSpanElement, TagProps>(function Tag(
  {
    classification = 'unclassified',
    kind,
    variant = 'subtle',
    size = 'md',
    code,
    onDismiss,
    dismissLabel,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const base = kind === undefined ? classificationToken(classification, 'base') : nodeKindToken(kind);

  const vars: StyleWithVars = {
    '--dpem-tag-base': base,
    '--dpem-tag-subtle': classificationToken(classification, 'subtle'),
    '--dpem-tag-border': classificationToken(classification, 'border'),
  };

  return (
    <span
      {...rest}
      ref={ref}
      data-classification={kind === undefined ? classification : kind}
      className={clsx(styles.tag, styles[variant], styles[size], className)}
      style={mergeStyle(vars, style)}
    >
      {code !== undefined && <span className={styles.code}>{code}</span>}
      <span className={styles.label}>{children}</span>
      {onDismiss !== undefined && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          data-focus-scope={variant === 'solid' ? 'inverse' : undefined}
        >
          <CloseIcon />
          <span className={styles.dismissLabel}>{dismissLabel ?? `Remove ${String(children)}`}</span>
        </button>
      )}
    </span>
  );
});
